// Cross-platform Bluetooth-printer helpers.
//
//  • Android (Capacitor)  → native ThermalPrinter plugin (RFCOMM/SPP). A device
//    is identified by its Bluetooth MAC address.
//  • Electron (desktop)   → a paired Bluetooth printer surfaces as a virtual
//    serial (COM) port, enumerated via the electron main process. A device is
//    identified by its COM/device path.
//  • Web                  → not supported (printing already requires the native
//    or desktop app).

import { Capacitor } from '@capacitor/core';
import Platform from '../utils/platform';

export interface BtDevice {
  id: string;    // MAC address (Android) or COM path (Electron)
  name: string;
  likelyPrinter?: boolean;
}

function plugin(): any {
  return (Capacitor as any).Plugins?.ThermalPrinter;
}

export const BluetoothPrinterService = {
  isSupported(): boolean {
    return Platform.isAndroid() || Platform.isElectron();
  },

  /** Ensure the runtime Bluetooth permission (Android 12+). No-op elsewhere. */
  async ensurePermission(): Promise<boolean> {
    if (Platform.isAndroid()) {
      const p = plugin();
      if (!p) return false;
      const res = await p.requestBluetoothPermissions();
      return !!res?.granted;
    }
    return true;
  },

  /** List selectable local devices: bonded BT devices (Android) or COM ports (Electron). */
  async listDevices(): Promise<BtDevice[]> {
    if (Platform.isAndroid()) {
      const p = plugin();
      if (!p) throw new Error('Bluetooth plugin unavailable');
      const res = await p.listBluetoothPrinters();
      const devices: any[] = res?.devices || [];
      return devices.map((d) => ({
        id: d.address,
        name: d.name || d.address,
        // 0x0600 = Bluetooth "Imaging" major device class (covers printers).
        likelyPrinter: (d.deviceClass & 0x0600) === 0x0600,
      }));
    }
    if (Platform.isElectron()) {
      const ports = await window.electronAPI.listSerialPorts();
      return (ports || []).map((p: any) => ({
        id: p.path,
        name: p.friendlyName || p.manufacturer ? `${p.path} — ${p.friendlyName || p.manufacturer}` : p.path,
        likelyPrinter: /bluetooth|bt|printer|com/i.test(`${p.friendlyName || ''} ${p.manufacturer || ''}`),
      }));
    }
    throw new Error('Bluetooth printing is only available in the tablet or desktop app');
  },

  /** Fire a small test print to the bound device. Returns true on success. */
  async testPrint(
    printer: { type?: string; hasAutoCutter?: boolean; ticketWidth?: number },
    deviceId: string,
  ): Promise<{ success: boolean; error?: string }> {
    const printerType = printer.type === 'Star Micronics' ? 'STAR' : 'EPSON';
    const html = testReceiptHtml();

    if (Platform.isAndroid()) {
      const p = plugin();
      if (!p) return { success: false, error: 'Bluetooth plugin unavailable' };
      try {
        const res = await p.printHtml({
          htmlContent: html,
          bluetoothAddress: deviceId,
          ticketWidthMm: printer.ticketWidth || 80,
          autoCutter: printer.hasAutoCutter ?? true,
          printerType,
        });
        return { success: !!res?.success };
      } catch (e: any) {
        return { success: false, error: e?.message || String(e) };
      }
    }

    if (Platform.isElectron()) {
      try {
        const res = await window.electronAPI.bluetoothTestPrint({
          port: deviceId,
          printerType,
          autoCutter: printer.hasAutoCutter ?? true,
          html,
        });
        return { success: !!res?.success, error: res?.error };
      } catch (e: any) {
        return { success: false, error: e?.message || String(e) };
      }
    }

    return { success: false, error: 'Unsupported platform' };
  },
};

function testReceiptHtml(): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:monospace;width:302px;margin:0;padding:8px;text-align:center;color:#000}
    h2{margin:6px 0;font-size:18px} .r{font-size:12px;margin:2px 0} hr{border:none;border-top:1px dashed #000;margin:8px 0}
  </style></head><body>
    <h2>TableView</h2>
    <div class="r">Bluetooth Printer Test</div>
    <hr/>
    <div class="r">If you can read this, the printer</div>
    <div class="r">is connected and working.</div>
    <hr/>
    <div class="r">*** TEST OK ***</div>
  </body></html>`;
}

export default BluetoothPrinterService;
